/**
 * @fileoverview Table styling toolbar block for the template editor.
 *
 * Rendered inline by `TemplateEditor` when the cursor is inside a table.
 * Surfaces the commands provided by `TableStyleExtension` plus
 * `TableKit`'s built-in structural ops (rows / cols / merge / split /
 * delete table) so practitioners can author tables that survive the
 * Word + PDF export pipeline (see `exportHtmlDocument.ts`).
 *
 * Mirrors the `HrContextControls` pattern in `template-editor.tsx`:
 * compact icon buttons + popovers, with the same colour palette as
 * the font-colour picker.
 */

import { useState } from "react";
import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import {
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  ArrowDownFromLine,
  ArrowLeftFromLine,
  ArrowRightFromLine,
  ArrowUpFromLine,
  BoxSelect,
  ChevronDown,
  Columns3,
  Eraser,
  Grid3x3,
  Merge,
  Minus,
  PaintBucket,
  Paintbrush,
  PanelBottom,
  PanelLeft,
  PanelRight,
  PanelTop,
  Rows3,
  SplitSquareHorizontal,
  Square,
  TableProperties,
  Trash2,
} from "lucide-react";

import { cn } from "@/utils/misc";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";

import { ToolbarButton, ToolbarDivider } from "./template-editor";
import { DEFAULT_CELL_BORDER, type BorderDecl } from "./table-style-extension";
import {
  type BorderSide,
  parseStyle,
  readBackground,
  readDominantBorder,
} from "./table-style-utils";

const BORDER_WIDTHS = ["0.5pt", "1pt", "1.5pt", "2pt"] as const;
type BorderWidth = (typeof BORDER_WIDTHS)[number];

const DEFAULT_HEADER_BACKGROUND = "#13294B";
const DEFAULT_HEADER_TEXT_COLOR = "#FFFFFF";

const DEFAULT_PALETTE = [
  "#000000",
  "#333333",
  "#666666",
  "#DCDCDC",
  "#13294B",
  "#1a56db",
  "#047857",
  "#8B1A1A",
  "#b91c1c",
  "#d97706",
] as const;

function isHexColor(value: string): boolean {
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value);
}

export interface TableContextControlsProps {
  editor: Editor;
  /** Org-customised swatch palette (falls back to DEFAULT_PALETTE). */
  presetColors?: string[] | null;
}

export function TableContextControls({
  editor,
  presetColors,
}: TableContextControlsProps) {
  const colors =
    presetColors && presetColors.length > 0 ? presetColors : DEFAULT_PALETTE;

  // The toolbar tracks the user's CURRENT border colour + width choice
  // locally, so successive clicks (preset, per-side toggles) all apply
  // the same look without re-prompting. Background is read live from
  // the selected cell.
  const [borderColor, setBorderColor] = useState<string>(
    DEFAULT_CELL_BORDER.color,
  );
  const [borderWidth, setBorderWidth] = useState<BorderWidth>(
    DEFAULT_CELL_BORDER.width as BorderWidth,
  );

  const currentDecl: BorderDecl = {
    width: borderWidth,
    style: "solid",
    color: borderColor,
  };

  // Picking a width / colour should ALSO update any borders that are
  // already on the table (or selection) — otherwise the user picks
  // "2pt" after applying a preset and nothing visible happens.
  // restyleBordersAuto is a no-op when there are no borders yet, so
  // we can call it unconditionally and just fall through to updating
  // local state for the next preset apply.
  const pickWidth = (w: BorderWidth) => {
    setBorderWidth(w);
    editor.chain().focus().restyleBordersAuto({ width: w }).run();
  };
  const pickColor = (c: string) => {
    setBorderColor(c);
    editor.chain().focus().restyleBordersAuto({ color: c }).run();
  };

  // Track the current cell so the "per-side" popover and background
  // swatch reflect the real state. `useEditorState` makes this
  // reactive without re-rendering the rest of the toolbar.
  const cellState = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e) return null;
      const attrs =
        e.getAttributes("tableHeader")?.style ??
        e.getAttributes("tableCell")?.style ??
        null;
      const styleMap = parseStyle(attrs);
      const dominant = readDominantBorder(styleMap);
      return {
        background: readBackground(styleMap),
        // Per-side active flags drive the four toggle buttons in
        // the per-side popover (highlighted when a border is set).
        sides: {
          top: hasBorder(styleMap.get("border-top")),
          right: hasBorder(styleMap.get("border-right")),
          bottom: hasBorder(styleMap.get("border-bottom")),
          left: hasBorder(styleMap.get("border-left")),
        },
        // If the cell HAS a dominant border AND the user hasn't
        // overridden colour/width yet, we surface those as a hint
        // (the toolbar's local state still wins for new actions).
        hintColor: dominant?.color ?? null,
        hintWidth: dominant?.width ?? null,
      };
    },
  });

  const handleToggleSide = (side: BorderSide) => {
    const hasSide =
      cellState?.sides[side] ?? readSideFromShorthand(side, cellState);
    editor
      .chain()
      .focus()
      .setCellBorderSide({
        side,
        decl: hasSide ? null : currentDecl,
      })
      .run();
  };

  return (
    <>
      <ToolbarDivider />

      {/* ---- Structural controls (existing functionality) ---- */}
      <ToolbarButton
        icon={ArrowUpFromLine}
        onClick={() => editor.chain().focus().addRowBefore().run()}
        disabled={!editor.can().addRowBefore()}
        title="Add row above"
      />
      <ToolbarButton
        icon={ArrowDownFromLine}
        onClick={() => editor.chain().focus().addRowAfter().run()}
        disabled={!editor.can().addRowAfter()}
        title="Add row below"
      />
      <ToolbarButton
        icon={ArrowLeftFromLine}
        onClick={() => editor.chain().focus().addColumnBefore().run()}
        disabled={!editor.can().addColumnBefore()}
        title="Add column before"
      />
      <ToolbarButton
        icon={ArrowRightFromLine}
        onClick={() => editor.chain().focus().addColumnAfter().run()}
        disabled={!editor.can().addColumnAfter()}
        title="Add column after"
      />
      <ToolbarButton
        icon={Rows3}
        onClick={() => editor.chain().focus().deleteRow().run()}
        disabled={!editor.can().deleteRow()}
        title="Delete row"
      />
      <ToolbarButton
        icon={Columns3}
        onClick={() => editor.chain().focus().deleteColumn().run()}
        disabled={!editor.can().deleteColumn()}
        title="Delete column"
      />
      <ToolbarButton
        icon={Merge}
        onClick={() => editor.chain().focus().mergeCells().run()}
        disabled={!editor.can().mergeCells()}
        title="Merge cells"
      />
      <ToolbarButton
        icon={SplitSquareHorizontal}
        onClick={() => editor.chain().focus().splitCell().run()}
        disabled={!editor.can().splitCell()}
        title="Split cell"
      />

      <ToolbarDivider />

      {/* ---- Border presets ---- */}
      <Popover modal>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 rounded px-1.5 py-1 text-primary/50 transition hover:bg-primary/5 hover:text-primary/70"
              >
                <Grid3x3 className="size-3.5" />
                <ChevronDown className="size-3" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Borders
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          className="w-56 p-2"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <PresetRow
            icon={Grid3x3}
            label="All borders"
            onClick={() =>
              editor
                .chain()
                .focus()
                .setTableBorderPreset({ preset: "all", decl: currentDecl })
                .run()
            }
          />
          <PresetRow
            icon={Square}
            label="Outer only"
            onClick={() =>
              editor
                .chain()
                .focus()
                .setTableBorderPreset({ preset: "outer", decl: currentDecl })
                .run()
            }
          />
          <PresetRow
            icon={Minus}
            label="Header underline"
            onClick={() =>
              editor
                .chain()
                .focus()
                .setTableBorderPreset({
                  preset: "header-bottom",
                  decl: currentDecl,
                })
                .run()
            }
          />
          <PresetRow
            icon={BoxSelect}
            label="No borders"
            onClick={() =>
              editor
                .chain()
                .focus()
                .setTableBorderPreset({ preset: "none" })
                .run()
            }
          />
        </PopoverContent>
      </Popover>

      {/* ---- Per-side toggles (selected cell) ---- */}
      <Popover modal>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 rounded px-1.5 py-1 text-primary/50 transition hover:bg-primary/5 hover:text-primary/70"
              >
                <BoxSelect className="size-3.5" />
                <ChevronDown className="size-3" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Per-side borders
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          className="w-44 p-2"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="grid grid-cols-4 gap-1 text-primary/60">
            <SideToggle
              icon={PanelTop}
              label="Top"
              active={cellState?.sides.top ?? false}
              onClick={() => handleToggleSide("top")}
            />
            <SideToggle
              icon={PanelRight}
              label="Right"
              active={cellState?.sides.right ?? false}
              onClick={() => handleToggleSide("right")}
            />
            <SideToggle
              icon={PanelBottom}
              label="Bottom"
              active={cellState?.sides.bottom ?? false}
              onClick={() => handleToggleSide("bottom")}
            />
            <SideToggle
              icon={PanelLeft}
              label="Left"
              active={cellState?.sides.left ?? false}
              onClick={() => handleToggleSide("left")}
            />
          </div>
          <p className="mt-2 border-t border-border pt-2 text-[10px] text-primary/40">
            Toggles each side on the selected cell(s) using the current border
            colour + width.
          </p>
        </PopoverContent>
      </Popover>

      {/* ---- Border colour ---- */}
      <Popover modal>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 rounded px-1.5 py-1 text-primary/50 transition hover:bg-primary/5 hover:text-primary/70"
              >
                <div className="flex flex-col items-center">
                  <Square className="size-3.5" />
                  <div
                    className="mt-0.5 h-[3px] w-3.5 rounded-full"
                    style={{ backgroundColor: borderColor }}
                  />
                </div>
                <ChevronDown className="size-3" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Border colour
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          className="w-44 p-3"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <ColorGrid value={borderColor} options={colors} onPick={pickColor} />
          <div className="mt-2 flex items-center gap-2 border-t border-border pt-2">
            <input
              type="color"
              value={isHexColor(borderColor) ? borderColor : "#000000"}
              onChange={(e) => pickColor(e.target.value)}
              className="h-6 w-6 cursor-pointer rounded border border-border bg-transparent p-0"
            />
            <span className="text-[10px] text-primary/40">Custom</span>
            <button
              type="button"
              onClick={() => pickColor(DEFAULT_CELL_BORDER.color)}
              className="ml-auto text-[10px] text-primary/40 hover:text-primary/70"
            >
              Reset
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* ---- Border width ---- */}
      <Popover modal>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 rounded px-1.5 py-1 text-xs font-medium text-primary/50 transition hover:bg-primary/5 hover:text-primary/70"
              >
                <span className="w-10 text-center text-[10px] tabular-nums">
                  {borderWidth}
                </span>
                <ChevronDown className="size-3" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Border width
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          className="w-32 p-1.5"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {BORDER_WIDTHS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => pickWidth(w)}
              className={cn(
                "flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs transition",
                borderWidth === w
                  ? "bg-primary/10 text-primary"
                  : "text-primary/60 hover:bg-primary/5 hover:text-primary/80",
              )}
            >
              <span className="tabular-nums">{w}</span>
              <span
                className="h-px w-8 rounded-full bg-current"
                style={{ height: widthPreviewPx(w) }}
              />
            </button>
          ))}
        </PopoverContent>
      </Popover>

      {/* ---- Cell background ---- */}
      <Popover modal>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 rounded px-1.5 py-1 text-primary/50 transition hover:bg-primary/5 hover:text-primary/70"
              >
                <div className="flex flex-col items-center">
                  <PaintBucket className="size-3.5" />
                  <div
                    className="mt-0.5 h-[3px] w-3.5 rounded-full border border-border/50"
                    style={{
                      backgroundColor: cellState?.background ?? "transparent",
                    }}
                  />
                </div>
                <ChevronDown className="size-3" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            Cell background
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          className="w-44 p-3"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <ColorGrid
            value={cellState?.background ?? null}
            options={colors}
            onPick={(c) => editor.chain().focus().setCellBackground(c).run()}
          />
          <div className="mt-2 flex items-center gap-2 border-t border-border pt-2">
            <input
              type="color"
              value={
                cellState?.background && isHexColor(cellState.background)
                  ? cellState.background
                  : "#FFFFFF"
              }
              onChange={(e) =>
                editor.chain().focus().setCellBackground(e.target.value).run()
              }
              className="h-6 w-6 cursor-pointer rounded border border-border bg-transparent p-0"
            />
            <span className="text-[10px] text-primary/40">Custom</span>
            <button
              type="button"
              onClick={() =>
                editor.chain().focus().setCellBackground(null).run()
              }
              className="ml-auto flex items-center gap-1 text-[10px] text-primary/40 hover:text-primary/70"
              title="Clear background"
            >
              <Eraser className="size-3" />
              Clear
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <ToolbarDivider />

      {/* ---- Vertical alignment ---- */}
      <ToolbarButton
        icon={AlignVerticalJustifyStart}
        onClick={() => editor.chain().focus().setCellVerticalAlign("top").run()}
        title="Align cell top"
      />
      <ToolbarButton
        icon={AlignVerticalJustifyCenter}
        onClick={() =>
          editor.chain().focus().setCellVerticalAlign("middle").run()
        }
        title="Align cell middle"
      />
      <ToolbarButton
        icon={AlignVerticalJustifyEnd}
        onClick={() =>
          editor.chain().focus().setCellVerticalAlign("bottom").run()
        }
        title="Align cell bottom"
      />

      <ToolbarDivider />

      {/* ---- Header row ---- */}
      <ToolbarButton
        icon={TableProperties}
        onClick={() => editor.chain().focus().toggleHeaderRow().run()}
        title="Toggle header row"
      />
      <ToolbarButton
        icon={Paintbrush}
        onClick={() =>
          editor
            .chain()
            .focus()
            .applyHeaderRowStyle({
              background: DEFAULT_HEADER_BACKGROUND,
              textColor: DEFAULT_HEADER_TEXT_COLOR,
              cellBorder: currentDecl,
            })
            .run()
        }
        title="Apply branded header style (navy background + white text)"
      />

      <ToolbarDivider />

      {/* ---- Delete table (kept last; matches existing behaviour) ---- */}
      <ToolbarButton
        icon={Trash2}
        onClick={() => editor.chain().focus().deleteTable().run()}
        disabled={!editor.can().deleteTable()}
        title="Delete table"
      />
    </>
  );
}

/** Returns true when the per-side border value exists AND isn't `none`. */
function hasBorder(value: string | undefined): boolean {
  if (!value) return false;
  return value.trim().toLowerCase() !== "none";
}

/**
 * When the cell uses the `border:` shorthand, every side is implicitly
 * set. Reflect that in the per-side toggles so clicking "top" actually
 * REMOVES that side rather than adding a redundant one.
 */
function readSideFromShorthand(
  _side: BorderSide,
  cellState: { sides: Record<BorderSide, boolean> } | null,
): boolean {
  return cellState?.sides
    ? (cellState.sides.top &&
        cellState.sides.right &&
        cellState.sides.bottom &&
        cellState.sides.left) ||
        false
    : false;
}

function widthPreviewPx(w: BorderWidth): string {
  switch (w) {
    case "0.5pt":
      return "1px";
    case "1pt":
      return "2px";
    case "1.5pt":
      return "3px";
    case "2pt":
      return "4px";
  }
}

// ---------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------

function PresetRow({
  icon: Icon,
  label,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-primary/70 transition hover:bg-primary/5 hover:text-primary"
    >
      <Icon className="size-3.5 shrink-0" />
      <span>{label}</span>
    </button>
  );
}

function SideToggle({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className={cn(
            "flex h-8 items-center justify-center rounded transition",
            active
              ? "bg-primary/10 text-primary"
              : "text-primary/40 hover:bg-primary/5 hover:text-primary/70",
          )}
        >
          <Icon className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function ColorGrid({
  value,
  options,
  onPick,
}: {
  value: string | null;
  options: readonly string[];
  onPick: (color: string) => void;
}) {
  return (
    <div className="grid grid-cols-5 gap-1.5">
      {options.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => onPick(color)}
          className={cn(
            "size-6 rounded-md border transition hover:scale-110",
            value && value.toLowerCase() === color.toLowerCase()
              ? "border-primary ring-1 ring-primary"
              : "border-border",
          )}
          style={{ backgroundColor: color }}
          title={color}
        />
      ))}
    </div>
  );
}
